import { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ActionIcon, Indicator, Popover, Text, Button, Divider, Group, CloseButton } from '@mantine/core';
import { IconBell } from '@tabler/icons-react';
import { enqueueSnackbar } from 'notistack';
import dayjs from 'dayjs';

import { useIsMobile } from 'hooks/use-responsive';
import { fetchNotifications, markAsReadApi, markAllAsReadApi, deleteNotificationApi, setFilter } from 'store/features/notificationSlice';
import { getNotificationUrl, isDialogNotification } from 'utils/notification-helpers';
import callAzureFunction from 'utils/callAzureFunctions';
import NotificationList from './NotificationList';
import FlightDealNotificationDialog from './FlightDealNotificationDialog';
import { shadows, radius } from 'theme';

// Lazy-load the heavy AI dialog only when needed
let AISearchCheapFlightDialog = null;
const loadAIDialog = () =>
  import('ui-components/dialogs/AISearchCheapFlightDialog').then((mod) => {
    AISearchCheapFlightDialog = mod.default;
  });

const parseDeal = (deal) => {
  let meta = {};
  try {
    meta = JSON.parse(deal.deal_meta) || {};
  } catch {
    meta = {};
  }
  return {
    ...meta,
    id: deal.id,
    home_airport_id: deal.home_airport_id,
    destination_airport_id: deal.destination_airport_id,
    origin: meta.origin || deal.home_airport_info?.iata,
    destination: meta.destination || deal.destination_airport_info?.iata,
    date_from: meta.date_from || deal.departure_date,
    date_to: meta.date_to || deal.return_date,
    trip_type: meta.trip_type || deal.travel_type,
    currency: meta.currency || 'USD',
    image_url: deal.image_url,
    price: deal.price
  };
};

const NotificationBell = () => {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { unreadCount, filter } = useSelector((state) => state.notification);

  const [popoverOpened, setPopoverOpened] = useState(false);

  // Dialog states
  const [flightDealMeta, setFlightDealMeta] = useState(null);
  const [aiDealData, setAiDealData] = useState(null);
  const [aiDialogReady, setAiDialogReady] = useState(false);

  const handleClose = useCallback(() => setPopoverOpened(false), []);

  // Toggle so the bell also dismisses; fetch fresh only when opening. The
  // dispatch stays out of the state updater: React may run an updater while
  // rendering (and twice in StrictMode), and a dispatch there updates
  // MainLayout mid-render.
  const handleToggle = useCallback(() => {
    if (!popoverOpened) dispatch(fetchNotifications({ filter }));
    setPopoverOpened((prev) => !prev);
  }, [dispatch, filter, popoverOpened]);

  const handleFlightDealClick = useCallback((notification) => {
    setFlightDealMeta(notification.metadata);
  }, []);

  const handleAIDealClick = useCallback(async (notification) => {
    try {
      // Load the dialog component if not yet loaded
      if (!AISearchCheapFlightDialog) await loadAIDialog();

      const response = await callAzureFunction({ url: 'ai-flight-deals', method: 'get' });
      const cutoff = dayjs().add(3, 'day').startOf('day');
      const deals = (response.data?.deals || []).map(parseDeal).filter((deal) => deal.date_from && !dayjs(deal.date_from).isBefore(cutoff));

      const match = deals.find((d) => d.id === notification.resource_id);
      if (match) {
        setAiDealData(match);
        setAiDialogReady(true);
      } else {
        enqueueSnackbar('This AI flight deal is no longer available.', {
          variant: 'info',
          anchorOrigin: { horizontal: 'right', vertical: 'top' }
        });
      }
    } catch {
      enqueueSnackbar('Failed to load AI flight deal.', {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' }
      });
    }
  }, []);

  const handleNotificationClick = useCallback(
    (notification) => {
      if (!notification.is_read) {
        dispatch(markAsReadApi(notification.id));
      }
      handleClose();

      if (isDialogNotification(notification.type)) {
        if (notification.type === 'flight_deal_match') {
          handleFlightDealClick(notification);
        } else if (notification.type === 'ai_flight_deal_ready') {
          handleAIDealClick(notification);
        }
        return;
      }

      const url = getNotificationUrl(notification.resource_type, notification.resource_id, notification.type);
      if (url) {
        navigate(url);
      }
    },
    [dispatch, navigate, handleClose, handleFlightDealClick, handleAIDealClick]
  );

  const handleMarkAllRead = useCallback(() => {
    dispatch(markAllAsReadApi());
  }, [dispatch]);

  const handleDelete = useCallback(
    (id) => {
      dispatch(deleteNotificationApi(id));
    },
    [dispatch]
  );

  const handleFilterChange = useCallback(
    (newFilter) => {
      dispatch(setFilter(newFilter));
      dispatch(fetchNotifications({ filter: newFilter }));
    },
    [dispatch]
  );

  return (
    <>
      <Popover
        opened={popoverOpened}
        onClose={handleClose}
        position="bottom-end"
        offset={8}
        width={isMobile ? 'calc(100vw - 32px)' : 400}
        shadow={shadows.popover}
        radius={radius.surface}
        styles={{
          dropdown: {
            maxWidth: 440,
            overflow: 'hidden',
            padding: 0
          }
        }}
      >
        <Popover.Target>
          <Indicator
            label={isMobile ? undefined : unreadCount}
            size={isMobile ? 10 : 16}
            color="red"
            disabled={unreadCount === 0}
            processing={isMobile && unreadCount > 0}
            offset={4}
          >
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={handleToggle}
              aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : 'Notifications'}
              color="gray"
            >
              <IconBell size={24} stroke={1.5} />
            </ActionIcon>
          </Indicator>
        </Popover.Target>

        <Popover.Dropdown>
          <Group justify="space-between" px={20} py={16} wrap="nowrap">
            <Text fw={700} size="md" style={{ letterSpacing: '-0.01em' }}>
              Notifications
            </Text>
            <Group gap="xs" wrap="nowrap">
              {unreadCount > 0 && (
                <Button variant="subtle" size="compact-xs" onClick={handleMarkAllRead} style={{ fontSize: '0.75rem' }}>
                  Mark all read
                </Button>
              )}
              <CloseButton onClick={handleClose} aria-label="Close notifications" size="sm" />
            </Group>
          </Group>
          <Divider />
          <NotificationList onNotificationClick={handleNotificationClick} onFilterChange={handleFilterChange} onDelete={handleDelete} />
        </Popover.Dropdown>
      </Popover>

      {/* Flight deal dialog */}
      <FlightDealNotificationDialog open={Boolean(flightDealMeta)} onClose={() => setFlightDealMeta(null)} metadata={flightDealMeta} />

      {/* AI flight deal dialog */}
      {aiDialogReady && AISearchCheapFlightDialog && (
        <AISearchCheapFlightDialog
          open={Boolean(aiDealData)}
          onClose={() => {
            setAiDealData(null);
            setAiDialogReady(false);
          }}
          initialData={aiDealData}
        />
      )}
    </>
  );
};

export default NotificationBell;
